import React, { useEffect, useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { Button, Card, Input, Tag, Row, Col, Spin, Alert } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as api from 'app/shared/api';
import { mapToRatesTable } from 'app/pages/AgentPricing/models';
import { RatesTableWidget } from 'app/pages/AgentPricing/components/RatesTableWidget/RatesTableWidget';
import { AgentCommissionStatus, AgentCommissionDto } from 'app/shared/model/AgentCommissionDto';
import { getAgentId, getAgentName, getAgentStatus, getOffices, getCountries, getSchools } from 'app/pages/AgentPricing/modules/RefData';
import { isAgentStopped } from 'app/pages/AgentPricing/utils/agentStatus';
import { getRatesTable } from 'app/pages/AgentPricing/modules/RatesTableWidget/selectors';
import { Country, School } from 'app/shared/model';
import { getSelectedDivisionId } from 'app/pages/AgentPricing/utils/division';
import { DateDisplay } from 'app/shared/ui/DateDisplay';
import './CompanyRatesSection.css';

export interface CompanyRatesSectionProps {}

interface StateProps {
    agentId: string;
    agentName: string;
    offices: { id: string; name: string; agentId: string }[];
    countries: Country[];
    schools: School[];
    divisionId: number | null;
    groupHasRates: boolean;
    groupEffectiveFrom?: string;
    isAgentStopped: boolean;
}

type OfficeStatus = 'Custom' | 'Group';

interface OfficeCommissionState {
    status: OfficeStatus;
    commission?: AgentCommissionDto;
}

type OfficeCommissionMap = Record<string, OfficeCommissionState>;

function isCurrentlyCustomCommission(commission: AgentCommissionDto): boolean {
    if (
        commission.status === AgentCommissionStatus.New ||
        commission.status === AgentCommissionStatus.Expired
    ) {
        return false;
    }

    if (commission.status === AgentCommissionStatus.FutureStart) {
        return true;
    }

    if (commission.endDate && dayjs().isAfter(dayjs(commission.endDate), 'day')) {
        return false;
    }

    return commission.status === AgentCommissionStatus.Active;
}

const CompanyRatesSection = (props: StateProps) => {
    const [selectedOfficeId, setSelectedOfficeId] = useState<string | null>(null);
    const [officeStatuses, setOfficeStatuses] = useState<OfficeCommissionMap>({});
    const [loadingStatuses, setLoadingStatuses] = useState(false);
    const [filter, setFilter] = useState<'All' | 'Custom' | 'Group'>('All');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (props.offices.length === 0 || !props.divisionId) return;

        setLoadingStatuses(true);
        Promise.all(
            props.offices.map(async (office) => {
                try {
                    const commission = await api.getLatestAgentCommission(office.id, props.divisionId!);
                    const isCustom = isCurrentlyCustomCommission(commission);
                    return { officeId: office.id, status: isCustom ? 'Custom' : 'Group', commission };
                } catch {
                    return { officeId: office.id, status: 'Group' as OfficeStatus, commission: undefined };
                }
            })
        )
            .then((results) => {
                const map: OfficeCommissionMap = {};
                results.forEach((r) => {
                    map[r.officeId] = { status: r.status as OfficeStatus, commission: r.commission };
                });
                setOfficeStatuses(map);
            })
            .finally(() => setLoadingStatuses(false));
    }, [props.offices.map((o) => o.id).join(','), props.divisionId]);

    const filteredOffices = useMemo(() => {
        let list = props.offices;
        if (search.trim()) {
            const q = search.toLowerCase();
            list = list.filter((o) => o.name.toLowerCase().includes(q));
        }
        if (filter !== 'All') {
            list = list.filter((o) => officeStatuses[o.id]?.status === filter);
        }
        return list;
    }, [props.offices, search, filter, officeStatuses]);

    const selectedOffice = selectedOfficeId ? props.offices.find((o) => o.id === selectedOfficeId) : null;
    const selectedData = selectedOfficeId ? officeStatuses[selectedOfficeId] : null;
    const isCustom = selectedData?.status === 'Custom';
    const commission = selectedData?.commission;

    const tableAndColumns = useMemo(() => {
        if (!commission || !props.countries.length || !props.schools.length) return null;
        const table = mapToRatesTable(commission, props.countries, props.schools, false);
        const columns: { id: string; name: string; isSchool: boolean }[] = [];
        table.countries.forEach((c) => c.columns.forEach((col) => columns.push(col)));
        return { table, columns };
    }, [commission, props.countries, props.schools]);

    const editRatesUrl = (scopeId: string) => {
        const div = props.divisionId ? `&divisionId=${props.divisionId}` : '';
        const groupParam = scopeId !== props.agentId ? `&groupId=${encodeURIComponent(props.agentId)}` : '';
        return `/agents/edit-rates?id=${encodeURIComponent(scopeId)}${groupParam}${div}`;
    };

    if (props.offices.length === 0) {
        return (
            <Card>
                <Alert
                    title="No offices"
                    description="This agent group has no offices. Company-level commission rates apply to offices."
                    type="info"
                />
            </Card>
        );
    }

    return (
        <div className="company-rates-section">
            <Row gutter={24} wrap={false} align="top">
                <Col flex="0 0 320px" className="company-rates-section__col-left">
                    <div className="company-rates-section__title">Companies (offices)</div>
                    <Input.Search
                        placeholder="Search company"
                        allowClear
                        onSearch={setSearch}
                        onChange={(e) => setSearch(e.target.value)}
                        className="company-rates-section__search"
                    />
                    <div className="company-rates-section__filters">
                        {(['All', 'Custom rates', 'Group rates'] as const).map((f) => {
                            const key = f === 'All' ? 'All' : f === 'Custom rates' ? 'Custom' : 'Group';
                            return (
                                <Button
                                    key={f}
                                    type={filter === key ? 'primary' : 'default'}
                                    size="small"
                                    onClick={() => setFilter(key)}
                                >
                                    {f}
                                </Button>
                            );
                        })}
                    </div>
                    {loadingStatuses ? (
                        <Spin />
                    ) : (
                        <div className="company-rates-section__office-list">
                            {filteredOffices.map((office) => {
                                const status = officeStatuses[office.id]?.status ?? 'Group';
                                const isSelected = selectedOfficeId === office.id;
                                return (
                                    <div
                                        key={office.id}
                                        onClick={() => setSelectedOfficeId(office.id)}
                                        className={`company-rates-section__office-item ${isSelected ? 'company-rates-section__office-item--selected' : ''}`}
                                    >
                                        <span>{office.name}</span>
                                        <Tag color={status === 'Custom' ? 'green' : 'default'}>{status}</Tag>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </Col>
                <Col flex="1 1 0" className="company-rates-section__col-right">
                    {!selectedOffice ? (
                        <Card>
                            <Alert title="Select an office" description="Choose an office from the list to view or add custom rates." type="info" />
                        </Card>
                    ) : isCustom && commission && tableAndColumns ? (
                        <Card
                            title={
                                <span className="company-rates-section__card-title">
                                    {selectedOffice.name}
                                    <Tag color="green">Custom</Tag>
                                    <span className="company-rates-section__card-title-date">
                                        Effective from: {DateDisplay(commission.startDate)}
                                        {commission.endDate && (
                                            <>, Reverts to group after {DateDisplay(commission.endDate)}</>
                                        )}
                                    </span>
                                </span>
                            }
                            extra={
                                <Button type="primary" icon={<EditOutlined />} href={props.isAgentStopped ? undefined : editRatesUrl(selectedOffice.id)} disabled={props.isAgentStopped}>
                                    Edit rates
                                </Button>
                            }
                        >
                            <RatesTableWidget
                                readonly
                                table={tableAndColumns.table}
                                columns={tableAndColumns.columns}
                                highlightInfo={{ columnId: '', ruleCode: '' }}
                                onChangeRate={() => {}}
                                onChangeStartDate={() => {}}
                            />
                        </Card>
                    ) : (
                        <Card
                            title={
                                <span className="company-rates-section__card-title">
                                    {selectedOffice.name}
                                    <Tag color="default">Group</Tag>
                                    {props.groupHasRates && props.groupEffectiveFrom && (
                                        <span className="company-rates-section__card-title-date">
                                            Effective from: {DateDisplay(props.groupEffectiveFrom)}
                                        </span>
                                    )}
                                </span>
                            }
                        >
                            <p className="company-rates-section__empty-message">
                                No custom rates created for this office. It is currently using the group&apos;s default rates.
                            </p>
                            <Button
                                type="primary" icon={<PlusOutlined />} href={props.isAgentStopped ? undefined : editRatesUrl(selectedOffice.id)} disabled={props.isAgentStopped}>
                                Add custom rates
                            </Button>
                        </Card>
                    )}
                </Col>
            </Row>
        </div>
    );
};

const mapStateToProps = (state: any): StateProps => {
    const divisionId = getSelectedDivisionId();
    const groupRatesTable = getRatesTable(state);
    const groupHasRates = groupRatesTable.status !== AgentCommissionStatus.New;

    return {
        agentId: getAgentId(state),
        agentName: getAgentName(state),
        offices: getOffices(state),
        countries: getCountries(state),
        schools: getSchools(state),
        divisionId: divisionId ?? null,
        groupHasRates,
        groupEffectiveFrom: groupHasRates ? groupRatesTable.dates.startDate : undefined,
        isAgentStopped: isAgentStopped(getAgentStatus(state)),
    };
};

export default connect(mapStateToProps)(CompanyRatesSection);
